"use strict";

import { httpClient } from "../utils/httpClient.js";

// This file contains API calls related to posts in the social web application

// This function is used to authenticate a user
// It takes a userId as input and returns the authentication data
// The userId is passed as a query parameter to the API endpoint
export const userAuthenticationApi = async userId => {
  const response = await httpClient.get(`/auth/sign?userId=${userId}`);
  return response.data;
};

// This function is used to create a new post
// It takes a PostData object as input and returns the created post data
export const createPost = async data => {
  const response = await httpClient.post("/post", data);
  return response.data;
};

// This function is used to edit an existing post
// It takes a PostData object with an _id field as input and returns the updated post data
export const editPost = async data => {
  const response = await httpClient.put(`/post/${data._id}`, {
    ...data
  });
  return response.data;
};

// This function is used to delete a post
export const deletePost = async postId => {
  const response = await httpClient.delete(`/post/${postId}`);
  return response.data;
};

// This function is used to like a post
export const likePost = async postId => {
  const response = await httpClient.post(`/like`, {
    postId
  });
  return response.data;
};

// This function is used to dislike a post
export const dislikePost = async postId => {
  const response = await httpClient.delete(`/like/${postId}`);
  return response.data;
};

// This function is used to follow a user
export const followUser = async (targetId, type, displayLabel) => {
  const response = await httpClient.post(`/follow`, {
    targetId,
    type,
    displayLabel
  });
  return response.data;
};

// This function is used to get users counts for following, followers, is follow, post count
export const getCountsForUser = async profileId => {
  let url = `/users/get-counts/${profileId}`;
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to unfollow a user
export const unfollowUser = async (targetId, type) => {
  const response = await httpClient.delete(`/follow/${targetId}?type=${type}`);
  return response.data;
};

// This function build params for GET requests
const buildQueryParams = paramsObj => {
  const params = new URLSearchParams();
  Object.entries(paramsObj).forEach(([key, value]) => {
    if (value == null) return; // skip null/undefined
    if (typeof value === "string" && value.trim() === "") return; // skip empty strings

    if (Array.isArray(value)) {
      value.forEach(v => {
        if (v != null && v.trim() !== "") params.append(key, v);
      });
    } else {
      params.append(key, value.toString().trim());
    }
  });
  return params.toString();
};
const buildPostQueryParams = paramsObj => {
  const params = {};
  Object.entries(paramsObj).forEach(([key, value]) => {
    if (value == null) return; // skip null/undefined
    if (typeof value === "string" && value.trim() === "") return; // skip empty strings

    if (Array.isArray(value)) {
      if (value.length > 0) {
        params[key] = value;
      }
    } else {
      params[key] = value;
    }
  });
  return params;
};

// This function is used to fetch the list of posts
export const getFeedList = async (paramsObj = {}) => {
  const query = buildQueryParams(paramsObj);
  const url = `/post${query ? `?${query}` : ""}`;
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to fetch the list of posts for the particular users
// It takes an optional lastId parameter to paginate the results
export const getUsersFeed = async (lastId, userId) => {
  let url = `/post?profileUserId=${userId}`;
  // If lastId is provided, append it as a query parameter
  if (lastId) {
    url += `&lastId=${lastId}`;
  }
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to fetch the list of tagged users who're added in a post
export const getTaggedUsers = async postId => {
  let url = `/post/tagged-profiles/${postId}`;
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to fetch the list of users who liked a post
export const getPostLikeList = async (postId, lastId) => {
  let url = `/like/user-list/${postId}`;
  // If lastId is provided, append it as a query parameter
  if (lastId) {
    url += `?lastId=${lastId}`;
  }
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to fetch the list of users who liked a comment
export const getCommentLikeList = async (commentId, postId, lastId) => {
  let url = `/like/comment-user-list/${commentId}?postId=${postId}`;
  // If lastId is provided, append it as a query parameter
  if (lastId) {
    url += `&lastId=${lastId}`;
  }
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to fetch list of comments or replies for particular post or comment
export const getCommentList = async (postId, lastId, parentId) => {
  let url = `/comment/${postId}`;
  const params = new URLSearchParams();

  // If lastId is provided, append it as a query parameter
  if (lastId) {
    params.append("lastId", lastId);
  }
  // To get reply list of particular comment, pass id of that comment
  if (parentId) {
    params.append("parentId", parentId);
  }
  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to report a post
export const postReport = async (postId, description) => {
  const response = await httpClient.post(`/post/report`, {
    postId,
    description
  });
  return response.data;
};
// This function is used to add comment on post or to reply any comment
export const addComment = async (postId, comment, parentId) => {
  let url = `/comment`;
  let addCommentData = {
    postId: postId,
    comment: comment
  };

  // To add reply to any comment, pass it's parent id
  if (parentId) {
    addCommentData.parentId = parentId;
  }
  const response = await httpClient.post(url, addCommentData);
  return response.data;
};

// This function is used to edit any comment or reply
export const updateComment = async (commentId, comment, postId) => {
  let url = `/comment/${commentId}`;
  const response = await httpClient.put(url, {
    comment,
    postId
  });
  return response.data;
};

// This function is used to delete a comment
export const deleteComment = async (commentId, postId, parentId) => {
  let url = `/comment/${commentId}?postId=${postId}`;

  // If parentId is provided, append it as a query parameter
  if (parentId) {
    url += `&parentId=${parentId}`;
  }
  const response = await httpClient.delete(url);
  return response.data;
};

// This function is used to like a comment
export const likeComment = async (commentId, postId) => {
  const response = await httpClient.post(`/like/comment`, {
    commentId,
    postId
  });
  return response.data;
};

// This function is used to dislike a comment
export const dislikeComment = async (commentId, postId) => {
  const response = await httpClient.delete(`/like/comment/${commentId}?postId=${postId}`);
  return response.data;
};

//This function is used for fetching user by search text
export const getUsersByKeyword = async keyword => {
  // encodeURIComponent ensures special characters in the keyword (like spaces, @, &, etc.)
  // are safely encoded so the URL doesn't break or cause errors
  const url = `/users/search/${encodeURIComponent(keyword)}`;
  const response = await httpClient.get(url);
  return response.data;
};

//This function is used for fetching requests list (Fundraiser, Fund, Drive) by search text
export const getRequestList = async keyword => {
  // encodeURIComponent ensures special characters in the keyword (like spaces, @, &, etc.)
  // are safely encoded so the URL doesn't break or cause errors
  const url = `/users/request-list/${encodeURIComponent(keyword)}`;
  const response = await httpClient.get(url);
  return response.data;
};

//This function is used for fetching categories
export const getCategories = async () => {
  const url = `/category`;
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to get data for post detail page
export const getPostDetailData = async postId => {
  let url = `/post/detail/${postId}`;
  const response = await httpClient.get(url);
  return response.data;
};

//This function is used to get data for edit post
export const getEditPostData = async postId => {
  let url = `/post/edit/${postId}`;
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used to vote on a poll
export const pollVote = async (postId, optionId) => {
  const response = await httpClient.post(`/post/poll/vote`, {
    postId,
    optionId
  });
  return response.data;
};
export const undoVote = async postId => {
  const response = await httpClient.post(`/post/poll/undo-vote`, {
    postId
  });
  return response.data;
};

// This function is used to get list of users who voted on a poll
export const getVoteUserList = async (postId, optionId, lastId) => {
  let url = `/post/poll/users?postId=${postId}&optionId=${optionId}`;
  if (lastId) {
    url += `&lastId=${lastId}`;
  }
  const response = await httpClient.get(url);
  return response.data;
};
export const getLinkedRequestsByPostId = async postId => {
  let url = `/post/linked-requests/${postId}`;
  const response = await httpClient.get(url);
  return response.data;
};

// This function is used archive post
export const archivePost = async postId => {
  const url = `/post/archive/${postId}`;
  const response = await httpClient.post(url);
  return response.data;
};

// This function is used to fetch the list of archived posts
export const getArchivedPosts = async (lastId, myPost) => {
  let url = `/post/archive-list`;
  // If lastId is provided, append it as a query parameter
  if (lastId) {
    url += `?lastId=${lastId}`;
  }
  // If myPost is true, append it as a query parameter
  if (myPost) {
    url += lastId ? `&myPost=${myPost}` : `?myPost=${myPost}`;
  }
  const response = await httpClient.get(url);
  return response.data;
};
export const getUserAccountList = async query => {
  const url = `/users/account-list`;
  let obj = buildPostQueryParams(query);
  const response = await httpClient.post(url, obj);
  return response.data;
};
export const getHashtagList = async (data = {}) => {
  const url = `/hashtag/list`;
  let obj = buildPostQueryParams(data);
  const response = await httpClient.post(url, obj);
  return response.data;
};
export async function uploadFilesToAzure(files, cachedSasUrl,
// Optional cached SAS URL
destUrlCache, cachePostId) {
  try {
    if (!files || files.length === 0) {
      throw new Error("No files provided for upload.");
    }

    // Get container SAS URL: use cached if available
    let containerSasUrl = cachedSasUrl;
    let containerDestUrl = destUrlCache || "";
    let postId = cachePostId || "";
    if (!containerSasUrl) {
      const response = await httpClient.get(`/media/upload-url`);
      postId = response && response.data && response.data.data && response.data.data.postId;
      containerDestUrl = response && response.data && response.data.data && response.data.data.destUrl;
      containerSasUrl = response && response.data && response.data.data && response.data.data.sasUrl;
    }
    if (!containerSasUrl) {
      throw new Error("Missing SAS URL in response.");
    }
    const [baseUrl, sasToken] = containerSasUrl.split("?");
    if (!baseUrl || !sasToken) {
      throw new Error("Invalid SAS URL format.");
    }
    const uploadedFiles = [];
    for (const file of files) {
      const publicBlobUrl = file && file.name && `${containerDestUrl}${file.name}`;
      const fullDestUrl = file && file.name && `${containerDestUrl}${file.name}?${sasToken}`;
      let fileBlob;
      if (file.url) {
        // If file has a local URL, fetch it first
        const fileResponse = await fetch(file.url);
        fileBlob = await fileResponse.blob();
      } else {
        fileBlob = file;
      }
      const uploadBlob = (blobUrl, blob) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", blobUrl, true);
          xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
          xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
          xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
          xhr.onerror = () => reject(new Error("Network error during upload"));
          xhr.ontimeout = () => reject(new Error("Upload timeout"));
          xhr.timeout = 30000;
          xhr.send(blob);
        });
      };
      await uploadBlob(fullDestUrl, fileBlob);
      uploadedFiles.push({
        name: file.name,
        type: file.type,
        size: file.size || fileBlob.size,
        url: publicBlobUrl
      });
    }
    return {
      uploaded: uploadedFiles,
      sasUrlUsed: containerSasUrl,
      postId: postId,
      destUrl: containerDestUrl
    };
  } catch (error) {
    console.error("❌ Upload error:", error);
    return {
      uploaded: [],
      sasUrlUsed: null,
      postId: "",
      destUrl: ""
    };
  }
}

// This function is used to get list of media
export const getMediaList = async data => {
  const url = `/post/media-list`;
  let obj = buildPostQueryParams(data);
  const response = await httpClient.post(url, obj);
  return response.data;
};
//# sourceMappingURL=posts.js.map